/**
 * Traduction minimale : un dictionnaire plat français, une fonction `t()`.
 *
 * Décision : une clé absente **lève**, elle ne renvoie jamais une chaîne vide ni la clé elle-même.
 * Une chaîne vide affichée en classe est un défaut invisible qui survit des mois ; une erreur
 * nommée se voit au premier rendu, en développement, et ne quitte jamais la machine.
 *
 * ## Un texte s'écrit pour celui qui le lit (7 septembre 2026)
 *
 * L'enseignant ouvre les écrans de ses élèves en permanence — pour montrer, pour préparer, pour
 * comprendre. Il y lisait des phrases écrites pour un élève : « Prévenez votre enseignant : c'est
 * lui qui distribue les jeux de données. » La personne qu'on lui demandait de prévenir, c'était
 * lui.
 *
 * La réparation n'est pas une retouche par message, c'est **une règle** en trois pièces :
 *
 * 1. **Une convention de clé.** Le message écrit pour l'élève garde sa clé ; la rédaction destinée
 *    à l'adulte vit sous la même clé suffixée de `.teacher`. Les deux sont ÉCRITES dans
 *    `messages/fr.json` — rien n'est traduit à la volée, rien n'est deviné.
 * 2. **Une résolution automatique.** `createT(messages, { reader })` choisit la variante à chaque
 *    appel. Aucun site d'appel ne change : `t('host.noWorkspaceHint')` rend la bonne phrase des
 *    deux côtés, y compris pour un message écrit demain par quelqu'un qui ignore que ce mécanisme
 *    existe.
 * 3. **Un garde-fou de test.** `studentAddressedKeys()` ci-dessous nomme les clés dont le texte
 *    s'adresse à un élève en parlant de « votre enseignant ». Le test du dictionnaire
 *    (`apps/desktop/tests/reader-messages.test.ts`) exige que chacune ait sa jumelle `.teacher` —
 *    un neuvième message écrit demain rougit avant d'atteindre une salle de classe.
 */

export type Messages = Record<string, string>;

export type TranslateVars = Record<string, string | number>;

export interface Translate<M extends Messages> {
  (key: keyof M & string, vars?: TranslateVars): string;
}

/**
 * Qui lit l'écran. `'student'` est le lecteur par défaut : c'est pour lui que la plateforme est
 * écrite, et c'est la rédaction qu'on sert tant qu'on ne sait pas encore qui est connecté.
 */
export type Reader = 'student' | 'teacher' | 'admin';

/**
 * Le suffixe de la rédaction destinée à l'adulte. Une seule convention, partout.
 *
 * C'est un suffixe, **pas un espace de noms réservé** : une clé ordinaire qui finirait par ces
 * huit caractères serait servie à l'adulte à la place de la clé dont elle a l'air d'être la
 * variante. Le cas s'est produit à la première exécution du garde-fou (`dock.teacher`, l'icône du
 * dock, renommée `dock.teacherSpace`) — c'est exactement ce que le troisième test de
 * `apps/desktop/tests/reader-messages.test.ts` surveille.
 */
export const TEACHER_SUFFIX = '.teacher';

export interface CreateTOptions {
  /**
   * Qui lit, demandé **à chaque appel**. Une fonction et non une valeur : le dictionnaire est
   * construit à l'import du module, bien avant que la session ne dise qui est connecté.
   */
  reader?: () => Reader;
}

const PLACEHOLDER = /\{(\w+)\}/g;

/**
 * Construit le `t()` d'un dictionnaire. Les variables s'écrivent `{nom}` dans le message ; une
 * variable citée par le message mais absente de l'appel lève elle aussi, pour la même raison.
 *
 * Quand `reader` désigne un adulte et que la clé possède une jumelle `<clé>.teacher`, c'est elle
 * qui est rendue. Sinon la clé demandée est rendue telle quelle : **un message qui décrit un fait
 * est vrai pour tout le monde** et n'a aucune raison d'avoir deux rédactions.
 */
export function createT<M extends Messages>(
  messages: M,
  options: CreateTOptions = {},
): Translate<M> {
  return (key, vars) => {
    const resolved = resolveKey(messages, key, options.reader?.() ?? 'student');
    if (!Object.hasOwn(messages, resolved)) {
      throw new Error(
        `Texte manquant : la clé « ${key} » n'existe pas dans messages/fr.json. ` +
          'Ajoutez-la plutôt que de laisser un texte en dur dans le code.',
      );
    }
    const template = messages[resolved] as string;
    return template.replace(PLACEHOLDER, (_match, name: string) => {
      const value = vars?.[name];
      if (value === undefined) {
        throw new Error(
          `Texte incomplet : le message « ${resolved} » attend la variable « ${name} », absente de l'appel.`,
        );
      }
      return String(value);
    });
  };
}

/** La clé réellement rendue : la jumelle `.teacher` si elle existe et si un adulte lit. */
function resolveKey(messages: Messages, key: string, reader: Reader): string {
  if (reader === 'student') return key;
  const variant = `${key}${TEACHER_SUFFIX}`;
  return Object.hasOwn(messages, variant) ? variant : key;
}

/**
 * **Le signe qui trahit un texte écrit pour un élève** : il parle à son lecteur de « votre
 * enseignant ».
 *
 * Volontairement étroit. Ce motif-là est le seul qui soit *toujours* faux quand un adulte le lit —
 * il désigne quelqu'un qui n'est pas dans la pièce, et parfois le lecteur lui-même. Une phrase qui
 * énonce un fait (« Aucun outil n'est associé à cette consigne ») reste vraie pour tout le monde ;
 * élargir le motif ferait rougir des messages qui n'ont rien à se reprocher, et un garde-fou qu'on
 * prend l'habitude de contourner ne garde plus rien.
 */
const ADDRESSED_TO_A_STUDENT = /\b(?:votre|vos|ton|tes)\s+(?:enseignant|professeur|prof)/iu;

/** Vrai quand ce texte s'adresse à un élève en parlant de son enseignant. */
export function addressesAStudent(text: string): boolean {
  return ADDRESSED_TO_A_STUDENT.test(text);
}

/**
 * Les clés dont le texte s'adresse à un élève — celles qui exigent une jumelle `.teacher`. Les
 * variantes `.teacher` elles-mêmes sont exclues d'office : c'est le test qui vérifie, séparément,
 * qu'aucune ne retombe dans le défaut qu'elle est censée réparer.
 */
export function studentAddressedKeys(messages: Messages): string[] {
  return Object.keys(messages)
    .filter((key) => !key.endsWith(TEACHER_SUFFIX))
    .filter((key) => addressesAStudent(messages[key] ?? ''))
    .sort();
}
